import { getActivePeerConnections } from './instrumentWebRTC'

// Default maximum number of concurrent WebRTC connections
const DEFAULT_MAX_CONNECTIONS = 200

interface BudgetConfig {
  maxConnections: number
  warningThreshold: number // Percentage (0-1) at which to emit warnings
  criticalThreshold: number // Percentage (0-1) at which to force cleanup
}

class WebRTCBudgetManager {
  private static instance: WebRTCBudgetManager
  private config: BudgetConfig
  private warningCallback?: (currentCount: number, max: number) => void
  private criticalCallback?: (currentCount: number, max: number) => void

  private constructor() {
    this.config = {
      maxConnections: DEFAULT_MAX_CONNECTIONS,
      warningThreshold: 0.8, // 80% of max connections
      criticalThreshold: 0.9, // 90% of max connections
    }
  }

  public static getInstance(): WebRTCBudgetManager {
    if (!WebRTCBudgetManager.instance) {
      WebRTCBudgetManager.instance = new WebRTCBudgetManager()
    }
    return WebRTCBudgetManager.instance
  }

  public configure(config: Partial<BudgetConfig>): void {
    this.config = {
      ...this.config,
      ...config,
    }
  }

  public setWarningCallback(
    callback: (currentCount: number, max: number) => void
  ): void {
    this.warningCallback = callback
  }

  public setCriticalCallback(
    callback: (currentCount: number, max: number) => void
  ): void {
    this.criticalCallback = callback
  }

  public async checkBudget(): Promise<{
    withinBudget: boolean
    currentCount: number
    maxConnections: number
    status: 'normal' | 'warning' | 'critical'
  }> {
    const connections = await getActivePeerConnections()
    const currentCount = connections.length
    const { maxConnections, warningThreshold, criticalThreshold } = this.config

    const usageRatio = currentCount / maxConnections
    let status: 'normal' | 'warning' | 'critical' = 'normal'

    if (usageRatio >= criticalThreshold) {
      status = 'critical'
      this.criticalCallback?.(currentCount, maxConnections)
    } else if (usageRatio >= warningThreshold) {
      status = 'warning'
      this.warningCallback?.(currentCount, maxConnections)
    }

    return {
      withinBudget: currentCount < maxConnections,
      currentCount,
      maxConnections,
      status,
    }
  }

  // Synchronous version for performance-critical paths
  public checkBudgetSync(currentCount: number): {
    withinBudget: boolean
    currentCount: number
    maxConnections: number
    status: 'normal' | 'warning' | 'critical'
  } {
    const { maxConnections, warningThreshold, criticalThreshold } = this.config

    const usageRatio = currentCount / maxConnections
    let status: 'normal' | 'warning' | 'critical' = 'normal'

    if (usageRatio >= criticalThreshold) {
      status = 'critical'
      this.criticalCallback?.(currentCount, maxConnections)
    } else if (usageRatio >= warningThreshold) {
      status = 'warning'
      this.warningCallback?.(currentCount, maxConnections)
    }

    return {
      withinBudget: currentCount < maxConnections,
      currentCount,
      maxConnections,
      status,
    }
  }

  public async canCreateNewConnection(): Promise<boolean> {
    const { withinBudget } = await this.checkBudget()
    return withinBudget
  }

  public getMaxConnections(): number {
    return this.config.maxConnections
  }
}

// Export a singleton instance
export const budgetManager = WebRTCBudgetManager.getInstance()

// Export types for TypeScript support
export type { BudgetConfig }
